import { ParsedUrlQuery } from 'querystring';
import { switchRoutes } from '~/core/router';

/**
 * Origin of the foam-fluid deployment. `NEXT_PUBLIC_FOAM_FLUID_URL` is a full
 * origin (scheme included) so local development can point at a local fluid
 * port; deployed environments keep using the host-only
 * `NEXT_PUBLIC_FOAM_FLUID_HOST`, which is always https.
 *
 * Empty when neither is configured, which disables the hand-off rather than
 * redirecting to `https://undefined`. Setting either var to an empty string is
 * therefore a local kill switch, mirroring `VITE_CLASSIC_APP_DOMAIN=` on the
 * fluid side — without it there was no way to run classic locally without it
 * trying to hand users off (FOAM-22396).
 */
export const FLUID_FOAM_URL =
  process.env.NEXT_PUBLIC_FOAM_FLUID_URL ||
  (process.env.NEXT_PUBLIC_FOAM_FLUID_HOST
    ? `https://${process.env.NEXT_PUBLIC_FOAM_FLUID_HOST}`
    : '');

const LOCAL_HOSTNAME = /^(?:localhost|127\.0\.0\.1|\[::1\]|.+\.local)$/;

/** A host served from the developer's own machine. */
export const isLocalHostname = (hostname: string): boolean =>
  LOCAL_HOSTNAME.test(hostname);

/**
 * True when handing off would take the user off their own machine.
 *
 * `.env` points `NEXT_PUBLIC_FOAM_FLUID_HOST` at a deployed fluid, so without
 * this guard a developer running classic locally whose account is on `fluid`
 * gets ejected to that deployment on first load and can never use their own
 * server. Deployed hosts are unaffected — neither side is local, so hand-offs
 * proceed. A local-to-local target stays allowed, so the round-trip is still
 * testable by pointing `NEXT_PUBLIC_FOAM_FLUID_URL` at a local fluid port
 * (FOAM-22396).
 */
export const isCrossEnvironmentHandOff = (
  currentHostname: string,
  targetOrigin: string,
): boolean => {
  if (!isLocalHostname(currentHostname)) return false;

  try {
    return !isLocalHostname(new URL(targetOrigin).hostname);
  } catch {
    // Unparseable origin — treat as unsafe rather than redirect somewhere odd.
    return true;
  }
};

/**
 * foam-fluid (`foam-web/apps/foam-fluid`) route paths that krill can deep-link
 * into. Keep in sync with `apps/foam-fluid/src/routes` — the generated
 * `apps/foam-fluid/src/routeTree.gen.ts` is the authoritative path list.
 *
 * The inverse map lives in `foam-web/apps/foam-fluid/src/lib/classicRoutes.ts`.
 * Changing a route on either side means changing both files (FOAM-22396).
 */
const FluidRoute = {
  home: '/',
  talentDirectory: '/talent-directory',
  agencySettings: '/agency-settings',
  settingsAccount: '/settings/account',
  settingsNotifications: '/settings/notifications',
  usage: '/data-insights/usage',
  search: '/search',
  contentFeed: '/content-feed',
  dailyBrief: '/daily-brief',
  watchlist: '/watchlist',
  lists: '/lists',
  list: (id: string) => `/lists/${id}`,
  mediaKits: '/media-kits',
  mediaKit: (id: string) => `/media-kits/${id}`,
  campaignReports: '/campaign-reports',
  talent: (slug: string) => `/talent/${slug}`,
  talentMediaKits: (slug: string) => `/talent/${slug}/media-kits`,
  talentNotes: (slug: string) => `/talent/${slug}/notes`,
} as const;

type FluidPathResolver = (params: ParsedUrlQuery) => string;

const first = (value: string | string[] | undefined): string =>
  Array.isArray(value) ? value[0] : value ?? '';

/**
 * Maps a krill route (a Next.js `router.pathname` pattern, e.g.
 * `/t/[creatorSlug]`) to the equivalent foam-fluid path. Krill routes without a
 * fluid counterpart (media packs, live content, platform connections, …) are
 * intentionally omitted — `resolveFluidPath` returns `null` for those so the
 * caller can decide whether to stay in krill or fall back to the fluid home.
 */
const KRILL_TO_FLUID_ROUTE: Record<string, FluidPathResolver> = {
  [switchRoutes.root]: () => FluidRoute.home,
  // Roster / talent listing
  [switchRoutes.myRoster]: () => FluidRoute.talentDirectory,
  [switchRoutes.myAgency]: () => FluidRoute.talentDirectory,
  [switchRoutes.agencyTalent]: () => FluidRoute.talentDirectory,
  // Settings. `switchRoutes.settingsAgency` shares its value with
  // `switchRoutes.agencySettings` ('/agency/settings'), so one entry covers both.
  [switchRoutes.agencySettings]: () => FluidRoute.agencySettings,
  [switchRoutes.agencyUsage]: () => FluidRoute.usage,
  // Fluid's `/personal-settings` only 302s to `/settings/account`; target the
  // canonical page directly so the user does not pay for a second redirect.
  [switchRoutes.settingsProfile]: () => FluidRoute.settingsAccount,
  [switchRoutes.profileSettings]: () => FluidRoute.settingsAccount,
  [switchRoutes.settingsNotificationsPreferences]: () =>
    FluidRoute.settingsNotifications,
  // Search. Krill's content search is fluid's *content feed*; fluid's `/search`
  // is the newer unified search screen, which mirrors krill's global search.
  [switchRoutes.contentSearch]: () => FluidRoute.contentFeed,
  [switchRoutes.globalSearch]: () => FluidRoute.search,
  // Discovery
  [switchRoutes.myDaily]: () => FluidRoute.dailyBrief,
  [switchRoutes.watchlist]: () => FluidRoute.watchlist,
  [switchRoutes.creatorCampaignReportList]: () => FluidRoute.campaignReports,
  // Lists
  [switchRoutes.savedLists]: () => FluidRoute.lists,
  [switchRoutes.savedListDetail]: ({ id }) => {
    const listId = first(id);
    return listId ? FluidRoute.list(listId) : FluidRoute.lists;
  },
  // Media kits
  [switchRoutes.mediaKits]: () => FluidRoute.mediaKits,
  [switchRoutes.mediaKitDetail]: ({ id }) => {
    const kitId = first(id);
    return kitId ? FluidRoute.mediaKit(kitId) : FluidRoute.mediaKits;
  },
  // Talent profile
  [switchRoutes.profileOverview]: ({ creatorSlug }) => {
    const slug = first(creatorSlug);
    return slug ? FluidRoute.talent(slug) : FluidRoute.talentDirectory;
  },
  [switchRoutes.talentProfileMediaKits]: ({ creatorSlug }) => {
    const slug = first(creatorSlug);
    return slug ? FluidRoute.talentMediaKits(slug) : FluidRoute.talentDirectory;
  },
  [switchRoutes.talentProfileTalentNotes]: ({ creatorSlug }) => {
    const slug = first(creatorSlug);
    return slug ? FluidRoute.talentNotes(slug) : FluidRoute.talentDirectory;
  },
};

/**
 * Resolves the foam-fluid path equivalent to the krill route the user is on, or
 * `null` when krill's route has no fluid counterpart.
 *
 * Only path params cross over. Search params are deliberately dropped: the two
 * apps share no query vocabulary. Krill's `view` is `list | grid | stacked |
 * card`, none of which fluid understands — `/my-roster?view=card` must not
 * become `/talent-directory?view=card`. Fluid's own list and media-kit screens
 * read `creators`/`owners`/`sort`/`dir`, which krill never emits. Forwarding
 * anything here would only carry meaningless state into the new URL.
 *
 * @param pathname Next.js `router.pathname` (the pattern, not the resolved URL)
 * @param query    Next.js `router.query` (used for the dynamic path params)
 */
export const resolveFluidPath = (
  pathname: string,
  query: ParsedUrlQuery = {},
): string | null => {
  const resolver = KRILL_TO_FLUID_ROUTE[pathname];
  if (!resolver) return null;
  return resolver(query);
};

/**
 * Builds the absolute foam-fluid URL to redirect to. Unmapped routes fall back
 * to the fluid home page — appropriate when the user has explicitly asked to
 * switch interfaces. For the *automatic* bounce use `resolveFluidPath` and
 * leave the user in krill when it returns `null`.
 */
export const getFluidRedirectUrl = (
  pathname: string,
  query: ParsedUrlQuery = {},
): string => `${FLUID_FOAM_URL}${resolveFluidPath(pathname, query) ?? '/'}`;
